const toUpBit = val => {
	if (val == null || val === '') return null
	const n = Number(val)
	if (n === 1) return 1
	if (n === 0) return 0
	return null
}

const toMs = value => {
	if (value == null || value === '') return null
	if (typeof value === 'number' && Number.isFinite(value)) {
		return value > 1e12 ? value : value < 1e11 ? value * 1000 : value
	}
	const ms = Date.parse(value)
	return Number.isFinite(ms) ? ms : null
}

/** Prometheus [ts, value] → { t: ms, v: 0|1|null }. */
export const prometheusToAvailability = (upSeries = []) =>
	upSeries.map(([ts, val]) => ({ t: Number(ts) * 1000, v: toUpBit(val) }))

/**
 * Дотягивает ряд до «сейчас»: редкий step (сутки) иначе обрывается вчера,
 * а текущий обрыв берётся из live up / startsAt алерта.
 */
export const closeAvailabilityToNow = (
	availability = [],
	{ nowMs = Date.now(), currentUp = null, downSince = null } = {},
) => {
	const now = Number(nowMs)
	if (!Number.isFinite(now)) return availability
	const current =
		currentUp === 1 || currentUp === 0 ? currentUp : toUpBit(currentUp)
	const downAt = toMs(downSince)

	let points = availability.filter(
		point => Number.isFinite(point.t) && point.t <= now,
	)

	if (current === 0 && downAt != null && downAt <= now) {
		points = points.filter(point => point.t < downAt)
		points.push({ t: downAt, v: 0 })
		if (downAt < now) points.push({ t: now, v: 0 })
		return points
	}

	if (!points.length) {
		return current == null ? [] : [{ t: now, v: current }]
	}

	const last = points[points.length - 1]
	const value = current != null ? current : last.v
	if (last.t < now) return [...points, { t: now, v: value }]
	return points
}

const pushGap = (out, from, to) => {
	if (Number.isFinite(from) && Number.isFinite(to) && to > from) {
		out.push({ v: null, from, to })
	}
}

/**
 * Сжимает подряд идущие одинаковые биты. Дыры длиннее maxGapMs — «нет данных»,
 * а не продолжение прошлого 0: иначе текущий обрыв закрашивает весь запрошенный range.
 */
export const collapseAvailabilitySegments = (
	availability = [],
	untilMs = null,
	{ fromMs = null, maxGapMs = null } = {},
) => {
	const out = []
	for (let i = 0; i < availability.length; i += 1) {
		const point = availability[i]
		if (!Number.isFinite(point?.t)) continue
		const last = out[out.length - 1]
		if (!last) {
			if (fromMs != null && point.t > fromMs) pushGap(out, fromMs, point.t)
			out.push({ v: point.v, from: point.t, to: point.t })
			continue
		}
		const gap = point.t - last.to
		const closesToNow = i === availability.length - 1 && last.v === point.v
		if (maxGapMs != null && gap > maxGapMs && !closesToNow) {
			pushGap(out, last.to, point.t)
			out.push({ v: point.v, from: point.t, to: point.t })
		} else if (last.v === point.v) {
			last.to = point.t
		} else {
			out.push({ v: point.v, from: point.t, to: point.t })
		}
	}
	if (untilMs != null && out.length && untilMs > out[out.length - 1].to) {
		out[out.length - 1].to = untilMs
	}
	return out
}

export const uptimeRatio = (availability = []) =>
	availability.filter(point => point.v === 1).length /
	Math.max(availability.length, 1)

/**
 * Сброс собственного времени работы видеорегистратора.
 * Обрыв связи его не сбрасывает: пока регистратор не гас, счётчик растёт вместе с часами.
 * Допуск — дрожание скрейпа, не «короткий обрыв».
 */
const REBOOT_SLACK_SEC = 120

export const rebootsFromUptime = (uptimeSeries = []) => {
	const points = []
	for (const pair of uptimeSeries) {
		const t = Number(pair?.[0])
		const uptime = Number(pair?.[1])
		if (!Number.isFinite(t) || !Number.isFinite(uptime) || uptime < 0) continue
		points.push({ t, uptime })
	}

	const reboots = []
	for (let i = 1; i < points.length; i += 1) {
		const prev = points[i - 1]
		const next = points[i]
		const elapsed = next.t - prev.t
		if (!(elapsed > 0)) continue
		const keptPace = next.uptime + REBOOT_SLACK_SEC >= prev.uptime + elapsed
		// После перезапуска новое время работы умещается в промежуток между опросами.
		// Застывший счётчик при живом регистраторе — нет.
		const fitsInGap = next.uptime <= elapsed + REBOOT_SLACK_SEC
		if (keptPace || !fitsInGap) continue
		const bootAt = Math.min(next.t, Math.max(next.t - next.uptime, prev.t))
		reboots.push({
			at: bootAt * 1000,
			ranForSec: Math.max(0, prev.uptime + (bootAt - prev.t)),
		})
	}
	return reboots.reverse()
}

/** Время скрейпа только пока регистратор отвечает. Офлайн и таймауты — разрывы. */
export const responseTimeAligned = (upSeries = [], durationSeries = []) => {
	const durByTs = new Map(
		(durationSeries || []).map(([ts, val]) => [Number(ts), parseFloat(val)]),
	)
	return (upSeries || []).map(([ts, upVal]) => {
		if (toUpBit(upVal) !== 1) return null
		const n = durByTs.get(Number(ts))
		return Number.isFinite(n) ? n : null
	})
}
